/* eslint-disable prefer-arrow/prefer-arrow-functions */
import hashIt from 'hash-it';
import includes from 'lodash/includes';
import isNumber from 'lodash/isNumber';
import range from 'lodash/range';
import { useEffect, useState } from 'react';
import { Subscription } from 'rxjs';
import { rawProvider } from '../data/providers/rawProvider';
import {
  IForceTimeData,
  IGraph,
  IRawTest,
  ITest,
  ITimingEvent,
} from '../data/types';
import { makeGraphData } from '../utils/formatters';
import { roundValue } from '../utils/math';

const allowedKeys = [
  'combined',
  'left',
  'right',
  'velocities',
  'power',
  'rsi',
  'displacements',
  'xForceLeft',
  'xForceRight',
  'yForceLeft',
  'yForceRight',
];

interface IGraphData {
  Combined: number;
  Left: number;
  Right: number;
  XLeft: number;
  XRight: number;
  YLeft: number;
  YRight: number;
  Velocity: number;
  Displacement: number;
  Power: number;
  Time: number;
  Hertz: number;
}

interface UseGraphDataResult {
  graphData: IGraphData[];
  graphRaw: IGraph;
  timingEvents: ITimingEvent[];
  dataMin: number;
  loading: boolean;
}

/**
 * Hook to fetch and process graph data for a test
 * @param test The test to fetch data for
 * @param enabled Whether to fetch data (defaults to true)
 */
export const useGraphData = (
  test: ITest,
  enabled: boolean = true
): UseGraphDataResult => {
  const [graphRaw, setGraphRaw] = useState<IGraph>({
    zForce: [],
    zForceLeft: [],
    zForceRight: [],
    xForceLeft: [],
    xForceRight: [],
    yForceLeft: [],
    yForceRight: [],
    power: [],
    velocity: [],
    rsi: [],
    displacement: [],
    hertz: 1000,
    target: null,
    systemWeight: null,
    plateModel: null,
    excludedWindows: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [processedData, setProcessedData] = useState<{
    graphData: IGraphData[];
    timingEvents: ITimingEvent[];
    dataMin: number;
  }>({
    graphData: [],
    timingEvents: [],
    dataMin: 0,
  });

  // Fetch raw data when test changes and enabled is true
  useEffect((): (() => void) => {
    if (!test?.id || !enabled) {
      return (): void => {};
    }

    setLoading(true);
    const subscriber = loadGraphData(test.id);

    return (): void => {
      subscriber.unsubscribe();
    };
  }, [hashIt(test), enabled]);

  // Process raw data when it changes
  useEffect((): void => {
    if (loading) return;

    const [graphData, timingEvents, dataMin] = processGraphData(graphRaw, test);
    setProcessedData({
      graphData,
      timingEvents,
      dataMin,
    });
  }, [graphRaw, hashIt(test), loading]);

  /**
   * @description load the graph data
   * @param testId
   * @returns
   */
  const loadGraphData = (testId: string): Subscription => {
    return rawProvider(testId).subscribe((rawData: IRawTest): void => {
      const graph = parseRawTestData(rawData);
      setGraphRaw(graph);
      setLoading(false);
    });
  };

  /**
   * @description parse the raw test data
   * @param rawData
   * @returns
   */
  const parseRawTestData = (rawData: IRawTest): IGraph => {
    const parsed = parseAllowedKeys(rawData);

    return {
      zForce: parsed.combined,
      zForceLeft: parsed.left,
      zForceRight: parsed.right,
      xForceLeft: parsed.xForceLeft,
      xForceRight: parsed.xForceRight,
      yForceLeft: parsed.yForceLeft,
      yForceRight: parsed.yForceRight,
      power: parsed.power,
      velocity: parsed.velocities,
      rsi: parsed.rsi,
      displacement: parsed.displacements,
      target: rawData.target,
      hertz: rawData.hertz ?? 1000,
      systemWeight: rawData.systemWeight,
      plateModel: rawData.plateModel,
      excludedWindows: rawData.excludedWindows ?? [],
    };
  };

  /**
   * @description parse the allowed keys
   * @param data
   * @returns
   */
  const parseAllowedKeys = (data: IRawTest): { [key: string]: number[] } => {
    return Object.keys(data).reduce(
      (
        acc: { [key: string]: number[] },
        key: string
      ): { [key: string]: number[] } => {
        if (includes(allowedKeys, key) && data[key]) {
          acc[key] = data[key];
        }
        return acc;
      },
      {} as { [key: string]: number[] }
    );
  };

  return {
    graphData: processedData.graphData,
    graphRaw,
    timingEvents: processedData.timingEvents,
    dataMin: processedData.dataMin,
    loading,
  };
};

/**
 * @description process the graph data
 * @param graphData
 * @param test
 * @returns
 */
export function processGraphData(
  graphData: IGraph | undefined,
  test: ITest
): [IGraphData[], ITimingEvent[], number] {
  if (!graphData) return [[], [], 0];

  const forceTimeData = makeGraphData(test, graphData);
  if (!forceTimeData) return [[], [], 0];

  const ticks = range(forceTimeData.length);
  const ftData: IGraphData[] = [];
  let mDataMin = 0;

  for (const tick of ticks) {
    const { dataPoint, lowestValue } = processTickData(forceTimeData, tick);
    mDataMin = Math.min(mDataMin, lowestValue);
    ftData.push(dataPoint);
  }

  return [ftData, forceTimeData.timingEvents, mDataMin];
}

/**
 * @description process the tick data
 * @param forceTimeData
 * @param tick
 * @returns
 */
function processTickData(
  forceTimeData: IForceTimeData,
  tick: number
): { dataPoint: IGraphData; lowestValue: number } {
  const lowestX = findLowestValue(
    forceTimeData.xForceLeft[tick]?.[1],
    forceTimeData.xForceRight[tick]?.[1]
  );

  const lowestY = findLowestValue(
    forceTimeData.yForceLeft[tick]?.[1],
    forceTimeData.yForceRight[tick]?.[1]
  );

  const lowestValue = Math.min(lowestX, lowestY);

  return {
    dataPoint: {
      Combined: roundValue(forceTimeData.zForce[tick]?.[1], 1),
      Left: roundValue(forceTimeData.zForceLeft[tick]?.[1], 1),
      Right: roundValue(forceTimeData.zForceRight[tick]?.[1], 1),
      XLeft: roundValue(forceTimeData.xForceLeft[tick]?.[1], 1),
      XRight: roundValue(forceTimeData.xForceRight[tick]?.[1], 1),
      YLeft: roundValue(forceTimeData.yForceLeft[tick]?.[1], 1),
      YRight: roundValue(forceTimeData.yForceRight[tick]?.[1], 1),
      Velocity: forceTimeData.velocity[tick]?.[1],
      Displacement: forceTimeData.displacement[tick]?.[1],
      Power: forceTimeData.power[tick]?.[1],
      Time: forceTimeData.zForce[tick]?.[0],
      Hertz: forceTimeData.hertz,
    },
    lowestValue,
  };
}

/**
 * @description find the lowest value
 * @param left
 * @param right
 * @returns
 */
export const findLowestValue = (left?: number, right?: number): number => {
  if (!isNumber(left)) return right ?? 0;
  if (!isNumber(right)) return left;
  return Math.min(left, right);
};
